'use client';

import { useEffect, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { Plus } from 'lucide-react';
import FadeInSection from '@/components/ui/FadeInSection';

interface FaqItem {
  q: string;
  a: string;
}

// Localized "Month Year" for the "Last updated" stamp. `ua` is this site's
// segment for Ukrainian; map it to the BCP-47 tag `uk` so Intl resolves it.
function formatMonthYear(locale: string) {
  const intlLocale = locale === 'ua' ? 'uk' : locale;
  // Slavic locales (pl/uk/ru) lower-case month names by grammar, but the
  // "Last updated" stamp wants a capitalized month — upper-case the first
  // letter (the month leads the string in every locale we ship).
  const formatted = new Intl.DateTimeFormat(intlLocale, { month: 'long', year: 'numeric' }).format(new Date());
  return formatted.charAt(0).toLocaleUpperCase(intlLocale) + formatted.slice(1);
}

export default function FaqSection() {
  const t = useTranslations('faq');
  const locale = useLocale();
  const items = t.raw('items') as FaqItem[];

  // Independent toggles — several answers can stay open while scanning.
  const [open, setOpen] = useState<Set<number>>(() => new Set());

  // "Last updated: <Month> <Year>" — recomputed on mount so it always follows
  // the real current date on the client instead of freezing at build time.
  const [updated, setUpdated] = useState(() => formatMonthYear(locale));
  useEffect(() => {
    setUpdated(formatMonthYear(locale));
  }, [locale]);

  const toggle = (index: number) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });

  // FAQPage structured data — mirrors the visible Q&A in the active locale for
  // rich results in search. Kept in sync with `items` above.
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };

  return (
    <section
      id="faq"
      className="scroll-mt-2 border-t border-surface-bright/15 px-margin-mobile py-section-gap lg:scroll-mt-4 lg:px-margin-desktop"
    >
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />

      <div className="mx-auto w-full max-w-3xl">
        <FadeInSection direction="right" distance={64}>
          <p className="text-label-caps uppercase tracking-widest text-primary">
            {t('eyebrow')}
            <span
              suppressHydrationWarning
              className="ml-2 font-normal normal-case tracking-normal text-on-surface-variant/70"
            >
              · {t('updated')}: {updated}
            </span>
          </p>
          <h2 className="mt-stack-md font-headline text-headline-lg font-bold tracking-[0.02em] text-on-surface">
            {t('headline')}
          </h2>
        </FadeInSection>

        <ul className="mt-stack-lg space-y-3">
          {items.map((item, i) => {
            const isOpen = open.has(i);
            const panelId = `faq-panel-${i}`;
            const buttonId = `faq-trigger-${i}`;

            return (
              <li key={item.q} className="forest-card overflow-hidden">
                <FadeInSection delay={i * 0.04}>
                  <h3>
                    <button
                      type="button"
                      id={buttonId}
                      aria-controls={panelId}
                      aria-expanded={isOpen}
                      onClick={() => toggle(i)}
                      className="flex w-full items-center gap-4 px-6 py-5 text-left transition-colors duration-300 hover:bg-surface-bright/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary md:px-8 md:py-6"
                    >
                      <span
                        className={`flex-1 font-headline text-headline-md font-bold transition-colors duration-300 ${
                          isOpen ? 'text-primary' : 'text-on-surface'
                        }`}
                      >
                        {item.q}
                      </span>
                      <span
                        aria-hidden="true"
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-primary/30 text-primary transition-colors duration-300"
                      >
                        <Plus
                          width={18}
                          height={18}
                          strokeWidth={1.75}
                          className={`transition-transform duration-300 ${isOpen ? 'rotate-45' : ''}`}
                        />
                      </span>
                    </button>
                  </h3>

                  <div
                    id={panelId}
                    role="region"
                    aria-labelledby={buttonId}
                    className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                      isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                    }`}
                  >
                    <div className="overflow-hidden">
                      <p className="px-6 pb-6 text-body-md text-on-surface-variant md:px-8 md:text-body-lg">
                        {item.a}
                      </p>
                    </div>
                  </div>
                </FadeInSection>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
